import { useTranslation } from '@mypoz/i18n';

export interface StorageTermsBlockProps {
  /** `expiredays` — days a parcel is held free from arrival. */
  freeDays: number | undefined;
  /** `destroydays` — days before an unclaimed parcel may be destroyed. */
  destroyAfterDays: number | undefined;
}

/**
 * How long a warehouse holds a parcel for nothing, and when an unclaimed one is destroyed.
 *
 * Both numbers come from the warehouse record itself (MYP-30), and they genuinely differ per
 * origin — Guangzhou holds for 30 days and destroys at 45, Korea and Thailand hold for 15 and
 * destroy at 30. That is why this cannot be one shared sentence on every card.
 *
 * What is deliberately *not* here is a price. The endpoint does carry storage-fee columns, but it
 * sends the same unconfigured placeholder row for all eight warehouses — 11/22/33/44/55 against
 * 1.00 through 6.00 — so rendering them would put invented money on screen with the authority of
 * having come from the vendor, which is worse than the honest silence of not showing a rate at
 * all. The days above are real; the fees are not, and stay off until the vendor configures them.
 *
 * Sits above the 注意事项 because it is the more actionable of the two: the notices describe what
 * happens after an order is quoted, this describes what happens while a parcel simply sits there,
 * which is the situation a customer is usually in when they open this page.
 */
export function StorageTermsBlock({ freeDays, destroyAfterDays }: StorageTermsBlockProps) {
  const { t } = useTranslation();

  if (freeDays === undefined && destroyAfterDays === undefined) return null;

  return (
    <div className="border-t border-line px-5 py-4 sm:px-6">
      <h3 className="text-sm font-semibold text-ink">{t('warehouses.storage.title')}</h3>

      <ul className="mt-1.5 space-y-1 text-sm text-muted">
        {freeDays !== undefined ? (
          <li>{t('warehouses.storage.free', { days: freeDays })}</li>
        ) : null}
        {destroyAfterDays !== undefined ? (
          <li>{t('warehouses.storage.destroy', { days: destroyAfterDays })}</li>
        ) : null}
      </ul>
    </div>
  );
}
